// === ./src/frontend/components/TestimonialsSection.jsx ===
import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  Rating,
  alpha,
} from "@mui/material";
import { Star, CheckCircle } from "@mui/icons-material";
import { slideInUp } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const TestimonialsSection = ({ theme }) => {
  const { lang } = useContext(TranslateContext);

  const testimonials = [
    {
      name: "Alex Rivera",
      role: lang?.testimRole1 || "Software Engineer",
      avatar: "AR",
      rating: 5,
      color: theme.palette.primary.main,
      text:
        lang?.testimText1 ||
        "I use TorrentCloud to grab Linux ISOs and open-source software archives for my homelab. The speed is insane — a 20 GB file in under 4 minutes. No VPN needed.",
    },
    {
      name: "Priya Nair",
      role: lang?.testimRole2 || "Content Creator",
      avatar: "PN",
      rating: 5,
      color: theme.palette.success.main,
      text:
        lang?.testimText2 ||
        "Finally a service that doesn't require me to keep my PC on all night. I add the magnet link before bed and everything is in my cloud by morning. Game changer.",
    },
    {
      name: "Marcus Chen",
      role: lang?.testimRole3 || "IT Administrator",
      avatar: "MC",
      rating: 5,
      color: theme.palette.warning.main,
      text:
        lang?.testimText3 ||
        "We use TorrentCloud to distribute large open-source packages across our team. The BunnyCDN integration means everyone gets fast local downloads.",
    },
    {
      name: "Sofia Müller",
      role: lang?.testimRole4 || "Researcher",
      avatar: "SM",
      rating: 5,
      color: theme.palette.info.main,
      text:
        lang?.testimText4 ||
        "The search feature is brilliant. I can find public domain datasets and academic archives without ever leaving the dashboard. Saves me so much time.",
    },
    {
      name: "James Okafor",
      role: lang?.testimRole5 || "Developer",
      avatar: "JO",
      rating: 5,
      color: theme.palette.secondary?.main || theme.palette.primary.main,
      text:
        lang?.testimText5 ||
        "The API access on the Pro plan is exactly what I needed to automate my workflow. Clean docs, fast responses, and the team is super responsive.",
    },
    {
      name: "Yuki Tanaka",
      role: lang?.testimRole6 || "Student",
      avatar: "YT",
      rating: 5,
      color: theme.palette.error.main,
      text:
        lang?.testimText6 ||
        "The free tier is genuinely useful — not crippled like most services. When I needed more storage I upgraded in seconds. Totally worth it.",
    },
  ];

  return (
    <Box id="testimonials" sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Chip
            label={lang?.testimChip || "Loved by Users"}
            size="small"
            sx={{
              mb: 2,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.03em",
              mb: 2,
            }}
          >
            {lang?.testimTitle || "What Our Users Say"}
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={0.5} mb={1}>
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                sx={{ fontSize: 20, color: theme.palette.warning.main }}
              />
            ))}
          </Stack>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 460, mx: "auto", lineHeight: 1.7 }}
          >
            {lang?.testimSubtitle ||
              "Trusted by thousands of users worldwide for fast, private cloud downloads."}
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="stretch">
          {testimonials.map((t, i) => (
            <Grid
              size={{ xs: 12, sm: 6, lg: 4 }}
              key={i}
              sx={{ display: "flex" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  animation: `${slideInUp} 0.6s ease both`,
                  animationDelay: `${i * 0.1}s`,
                }}
              >
                <Card
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: `1px solid ${alpha(t.color, 0.12)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 16px 40px ${alpha(t.color, 0.12)}`,
                      borderColor: alpha(t.color, 0.25),
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "3.5rem",
                        lineHeight: 0.8,
                        color: alpha(t.color, 0.2),
                        fontFamily: "Georgia, serif",
                        fontWeight: 900,
                        mb: 1,
                      }}
                    >
                      "
                    </Typography>
                    <Rating
                      value={t.rating}
                      readOnly
                      size="small"
                      sx={{
                        mb: 1.5,
                        "& .MuiRating-iconFilled": {
                          color: theme.palette.warning.main,
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.75,
                        mb: 2.5,
                        fontStyle: "italic",
                        flex: 1,
                      }}
                    >
                      "{t.text}"
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          background: `linear-gradient(135deg, ${t.color}, ${alpha(t.color, 0.6)})`,
                          boxShadow: `0 4px 12px ${alpha(t.color, 0.3)}`,
                        }}
                      >
                        {t.avatar}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ lineHeight: 1.2 }}
                        >
                          {t.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.role}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: "auto" }}>
                        <CheckCircle
                          sx={{ fontSize: 16, color: "success.main" }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
